import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Badge } from '../../components/Badge';
import { Monitor, RefreshCw, Trash2, User, Laptop, Download, ShieldAlert, Sparkles, AlertTriangle, Volume2, QrCode, Edit3 } from 'lucide-react';
import { EnrollmentQrModal } from '../../components/EnrollmentQrModal';
import { EditDeviceModal } from '../../components/EditDeviceModal';
import { DOWNLOAD_LINKS } from '../../lib/downloadLinks';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';

export function DevicesPage() {
  const [devices, setDevices] = useState<any[]>([]);
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ringingId, setRingingId] = useState<string | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrUser, setQrUser] = useState<any>(null);
  const [editingDevice, setEditingDevice] = useState<any | null>(null);

  const fetchDevices = async () => {
    try {
      const [devRes, subRes] = await Promise.all([
        api.admin.getDevices(),
        api.admin.getSubscription().catch(() => ({ subscription: null })),
      ]);
      setDevices(devRes.devices);
      setSubscription(subRes.subscription);
    } catch (err) {
      console.error('Failed to load devices:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleRefresh = () => {
    haptics.tap();
    sounds.playTapSound();
    setRefreshing(true);
    fetchDevices();
  };

  const handleRevokeDevice = async (device: any) => {
    if (!confirm(`Revoke workstation "${device.deviceName || device.hostname}"? The agent will stop reporting.`)) {
      return;
    }
    try {
      haptics.impact();
      await api.admin.revokeDevice(device.id);
      sounds.playChimeSound();
      await fetchDevices();
    } catch (err: any) {
      alert(err.message || 'Failed to revoke device');
    }
  };

  const handleRingDevice = async (device: any) => {
    try {
      setRingingId(device.id);
      haptics.impact();
      sounds.playTapSound();
      const res = await api.admin.ringDevice(device.id);
      sounds.playChimeSound();
      alert(res.message || `Audio chime dispatched to ${device.deviceName || device.hostname}!`);
    } catch (err: any) {
      alert(err.message || 'Failed to dispatch audio command to device');
    } finally {
      setRingingId(null);
    }
  };

  const handleDeviceSaved = (updated: any) => {
    setDevices((prev) =>
      prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d))
    );
    fetchDevices();
  };

  const maxDev = subscription?.maxDevices !== undefined ? Number(subscription.maxDevices) : 50;
  const isUnlimited = maxDev >= 9999;
  const enrolledCount = devices.length;
  const isAtLimit = !isUnlimited && enrolledCount >= maxDev;
  const isNearLimit = !isUnlimited && enrolledCount >= maxDev * 0.8;
  const planName = subscription?.plan || (subscription?.duration === 'SEVEN_DAYS' ? 'TRIAL' : 'PRO');

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-5 sm:space-y-8 max-w-7xl mx-auto w-full min-w-0 animate-ios-enter">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white tracking-tight">Enrolled Devices</h1>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-medium">
            Registered Windows workstations reporting to this workspace
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={() => {
              haptics.selection();
              setQrUser({ fullName: 'New Workstation', email: '', id: 'device' });
              setQrModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-blue-600/15 hover:bg-blue-600/25 text-blue-600 dark:text-blue-400 border border-blue-500/25 ios-press transition-all"
            title="Generate Dynamic QR code to pair a new PC"
          >
            <QrCode className="w-3.5 h-3.5" />
            <span>Pair via QR</span>
          </button>

          <a
            href={DOWNLOAD_LINKS.WINDOWS_EXE}
            download="CKPulse.Agent.exe"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-md shadow-pulse-blue/20 ios-press transition-all"
            title="Download trusted Windows Agent from GitHub Releases (v1.0.0)"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Download Agent</span>
          </a>

          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-xl bg-white/80 dark:bg-zinc-900/80 hover:bg-white dark:hover:bg-zinc-800 text-zinc-800 dark:text-zinc-200 border border-black/[0.08] dark:border-white/10 ios-press shadow-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Plan Quota Card */}
      <div
        className={`p-4 sm:p-5 rounded-2xl ios-card transition-all ${
          isAtLimit
            ? 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300'
            : isNearLimit
            ? 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300'
            : 'border-black/[0.06] dark:border-white/[0.08]'
        }`}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div
              className={`p-2.5 rounded-2xl border shadow-sm ${
                isAtLimit
                  ? 'bg-rose-500/15 border-rose-500/30 text-rose-500'
                  : 'bg-pulse-blue/10 border-pulse-blue/20 text-pulse-blue'
              }`}
            >
              <Monitor className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-zinc-900 dark:text-white text-xs">
                  {planName} Plan Workstation Quota
                </span>
                <span
                  className={`px-2 py-0.5 text-[10px] font-bold rounded-full font-mono ${
                    planName === 'TRIAL'
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
                      : planName === 'STARTER'
                      ? 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30'
                      : 'bg-pulse-blue/15 text-pulse-blue border border-pulse-blue/30'
                  }`}
                >
                  {planName} TIER
                </span>
              </div>
              <p className="text-zinc-500 dark:text-zinc-400 text-[11px] mt-0.5 font-medium">
                {isAtLimit
                  ? `Limit reached: All ${maxDev} workstation seats are currently enrolled.`
                  : isUnlimited
                  ? 'Your enterprise organization has unlimited workstation seat capacity.'
                  : `${enrolledCount} of ${maxDev} workstations enrolled (${Math.round((enrolledCount / maxDev) * 100)}% utilized).`}
              </p>
            </div>
          </div>

          {!isUnlimited && (
            <div className="w-full md:w-64 flex flex-col gap-1.5">
              <div className="flex justify-between text-[11px] font-mono">
                <span className="text-zinc-500 dark:text-zinc-400">Seat Utilization</span>
                <span className={`font-bold ${isAtLimit ? 'text-rose-500' : isNearLimit ? 'text-amber-500' : 'text-zinc-900 dark:text-white'}`}>
                  {enrolledCount} / {maxDev} PCs
                </span>
              </div>
              <div className="w-full h-2.5 bg-black/5 dark:bg-zinc-950 rounded-full overflow-hidden border border-black/5 dark:border-zinc-800">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    isAtLimit ? 'bg-rose-500' : isNearLimit ? 'bg-amber-500' : 'bg-pulse-blue'
                  }`}
                  style={{ width: `${Math.min(100, (enrolledCount / maxDev) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {isAtLimit && (
          <div className="mt-3 pt-3 border-t border-rose-500/20 text-[11px] text-rose-600 dark:text-rose-300 flex items-center gap-2 font-medium">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>
              To enroll additional workstations, revoke an inactive device from the list below or contact your administrator to upgrade your plan.
            </span>
          </div>
        )}
      </div>

      {/* Mobile Device Cards (Visible on mobile, hidden on md+) */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 ios-card rounded-2xl text-xs">
            Loading workstations...
          </div>
        ) : devices.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 ios-card rounded-2xl text-xs">
            No workstations registered yet.
          </div>
        ) : (
          devices.map((d) => (
            <div key={d.id} className="p-4 rounded-2xl ios-card space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-black/5 dark:bg-zinc-800 flex items-center justify-center text-zinc-500 dark:text-zinc-400 shrink-0">
                    <Laptop className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-zinc-900 dark:text-white text-sm truncate">{d.deviceName || d.hostname}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">{d.deviceId}</div>
                  </div>
                </div>
                <Badge status={d.status} />
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs pt-2.5 border-t border-black/5 dark:border-white/[0.06]">
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase font-semibold">Assigned User</div>
                  <div className="text-zinc-800 dark:text-zinc-300 font-medium truncate mt-0.5">
                    {d.user ? d.user.fullName : <span className="italic text-zinc-400">Unassigned</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase font-semibold">OS / Agent</div>
                  <div className="text-zinc-800 dark:text-zinc-300 font-mono truncate mt-0.5">{d.os} (v{d.agentVersion})</div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2.5 border-t border-black/5 dark:border-white/[0.06] text-[11px] text-zinc-500 dark:text-zinc-400">
                <span>Last seen: {d.lastActivityAt ? new Date(d.lastActivityAt).toLocaleTimeString() : '—'}</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      haptics.selection();
                      sounds.playTapSound();
                      setEditingDevice(d);
                    }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-xl text-pulse-blue bg-pulse-blue/10 hover:bg-pulse-blue/20 border border-pulse-blue/30 ios-press font-semibold"
                    title="Rename workstation or attach staff member"
                  >
                    <Edit3 className="w-3 h-3" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleRingDevice(d)}
                    disabled={ringingId === d.id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-xl text-amber-600 dark:text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 ios-press font-semibold disabled:opacity-50"
                    title="Play remote sound chime on this workstation"
                  >
                    <Volume2 className="w-3 h-3" />
                    <span>{ringingId === d.id ? '...' : 'Ring'}</span>
                  </button>
                  <button
                    onClick={() => handleRevokeDevice(d)}
                    className="px-2.5 py-1 text-xs rounded-xl text-rose-600 dark:text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 ios-press font-semibold"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Devices Table (Hidden on mobile, visible on md+) */}
      <div className="hidden md:block ios-card rounded-2xl overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
            <thead className="bg-black/[0.03] dark:bg-zinc-900/90 text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-bold border-b border-black/[0.06] dark:border-white/[0.08]">
              <tr>
                <th className="py-3.5 px-4">Device Name</th>
                <th className="py-3.5 px-4">Assigned User</th>
                <th className="py-3.5 px-4">OS & Agent</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4">Last Seen</th>
                <th className="py-3.5 px-4">Last Activity</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.04] dark:divide-white/[0.05]">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-zinc-500 font-medium">
                    Loading workstation inventory...
                  </td>
                </tr>
              ) : devices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-zinc-500 font-medium">
                    No devices registered yet. Go to Users to generate an enrollment code.
                  </td>
                </tr>
              ) : (
                devices.map((d) => (
                  <tr key={d.id} className="hover:bg-black/[0.02] dark:hover:bg-zinc-800/40 transition-colors">
                    <td className="py-3.5 px-4 font-bold text-zinc-900 dark:text-white">
                      <div className="flex items-center gap-2.5">
                        <Laptop className="w-4 h-4 text-zinc-400" />
                        <div>
                          <span>{d.deviceName || d.hostname}</span>
                          <div className="text-[10px] font-mono text-zinc-500">{d.deviceId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      {d.user ? (
                        <div>
                          <div className="text-zinc-900 dark:text-white font-bold">{d.user.fullName}</div>
                          <div className="text-[11px] text-zinc-500">{d.user.email}</div>
                        </div>
                      ) : (
                        <span className="text-zinc-400 italic font-medium">Unassigned</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-zinc-600 dark:text-zinc-400 font-medium">
                      <div>{d.os}</div>
                      <div className="text-[10px] text-zinc-500 font-mono">v{d.agentVersion}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <Badge status={d.status} />
                    </td>
                    <td className="py-3.5 px-4 text-zinc-500 dark:text-zinc-400 font-medium">
                      {new Date(d.lastSeenAt).toLocaleTimeString()}
                    </td>
                    <td className="py-3.5 px-4 text-zinc-500 dark:text-zinc-400 font-medium">
                      {d.lastActivityAt ? new Date(d.lastActivityAt).toLocaleTimeString() : '—'}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            haptics.selection();
                            sounds.playTapSound();
                            setEditingDevice(d);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl text-pulse-blue bg-pulse-blue/10 hover:bg-pulse-blue/20 border border-pulse-blue/30 ios-press transition-colors"
                          title="Rename workstation or attach staff member"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleRingDevice(d)}
                          disabled={ringingId === d.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl text-amber-700 dark:text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 ios-press transition-colors disabled:opacity-50"
                          title="Ring audio chime on this workstation"
                        >
                          <Volume2 className="w-3.5 h-3.5" />
                          <span>{ringingId === d.id ? 'Ringing...' : 'Ring Alert'}</span>
                        </button>
                        <button
                          onClick={() => handleRevokeDevice(d)}
                          className="px-3 py-1.5 text-xs font-bold rounded-xl text-rose-600 dark:text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 ios-press transition-colors"
                        >
                          Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EnrollmentQrModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        user={qrUser}
        orgName={subscription?.organization?.name}
      />

      <EditDeviceModal
        isOpen={!!editingDevice}
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        onSaved={handleDeviceSaved}
      />
    </div>
  );
}
